import {
  type RequestQuery,
  type ResponseData,
  useApiQuery,
} from '@vrplatform/data';
import { useBalanceSheetTableFilter } from './useBalanceSheetTableFilter';
import { hasValue, sum } from '@vrplatform/utils';
import { useMemo } from 'react';

export const STORYBOOK_BALANCE_SHEET_TABLE_QUERY_KEY = 'balance-sheet-table';

export type BalanceSheetRow = {
  id: string;
  title: string;
  values: RowValues;
  subRows: BalanceSheetRow[];
  accountId: string | undefined;
  categoryId: string | undefined;
  columns: { id: string; title: string }[];
  type: 'group' | 'category' | 'account';
  total: number;
};

type PickByKey<T, K extends PropertyKey> = T extends Record<K, any> ? T : never;

type BalanceSheet = NonNullable<
  ResponseData<'get:/reports/balance-sheet'>['balanceSheet']
>;

type GroupRow = NonNullable<BalanceSheet['groups']>[0];
type CategoryRow = PickByKey<Required<GroupRow>, 'categories'>['categories'][0];
type AccountRow = NonNullable<CategoryRow['accounts']>[0];
type AnyRowType = (GroupRow | BalanceSheet | CategoryRow | AccountRow) & {
  type?: BalanceSheetRow['type'];
};

type RowValues = {
  [columnId: string]: {
    title: string;
    total: number;
  };
};

export type BalanceSheetTableData = {
  columns: {
    id: string;
    title: string;
  }[];
  rows: BalanceSheetRow[];
};

const filterNonZeroRows = (rows: BalanceSheetRow[]): BalanceSheetRow[] => {
  return rows
    .map<BalanceSheetRow | undefined>((row) => {
      const columnValues = Object.values(row.values);
      const subRows = filterNonZeroRows(row.subRows);

      if (
        subRows.length === 0 &&
        columnValues.every((value) => value.total === 0)
      ) {
        return undefined;
      }

      return {
        ...row,
        subRows,
      };
    })
    .filter(hasValue);
};

export const getBalanceSheetQueryFilterParams = (
  args: Omit<
    ReturnType<typeof useBalanceSheetTableFilter>['filter'],
    'date' | 'view'
  >
): RequestQuery<'get:/reports/balance-sheet'> => {
  return {
    startDate: args.startDate,
    endDate: args.endDate,
    ledger: args.ledger ?? undefined,
    view: args.type,
  };
};

const formatValues = (
  v: {
    id: string;
    title: string;
    total: number;
  }[]
) =>
  v.reduce(
    (acc, curr) => {
      if (!acc[curr.id]) {
        acc[curr.id] = {
          title: curr.title,
          total: curr.total,
        };
      } else {
        acc[curr.id].total += curr.total;
      }

      return acc;
    },
    {} as Record<
      string,
      {
        title: string;
        total: number;
      }
    >
  );

// Structure
// groups
//  - groups
//    - categories
//      - accounts
const buildRowId = (
  type: BalanceSheetRow['type'],
  entityId: string,
  parentRowId?: string
) => {
  const localId = `${type}:${entityId}`;
  return parentRowId ? `${parentRowId}/${localId}` : localId;
};

const formatAmbigiousRow = (
  row: AnyRowType,
  parentRowId?: string
): BalanceSheetRow => {
  const groupSubRows = 'groups' in row ? (row.groups ?? []) : [];
  const categorySubRows = 'categories' in row ? (row.categories ?? []) : [];

  const accountSubRows =
    'accounts' in row
      ? (() => {
          return (row.accounts ?? []).map<GroupRow>((account) => ({
            id: account.id,
            title: account.title,
            total: account.total,
            values: account.values.map((value) => ({
              id: value.id,
              title: value.title,
              total: value.total,
            })),
            type: 'account',
          }));
        })()
      : [];

  const isCategory = 'accounts' in row;
  const type = row.type ?? (isCategory ? 'category' : 'group');
  const rowId = buildRowId(type, row.id, parentRowId);

  const subRows = (
    groupSubRows.length
      ? groupSubRows
      : categorySubRows.length
        ? categorySubRows
        : accountSubRows
  ).map((subRow) => formatAmbigiousRow(subRow, rowId));

  const columns =
    'values' in row
      ? (row.values?.map((v) => ({
          id: v.id,
          title: v.title,
        })) ?? [])
      : (subRows?.[0]?.columns ?? []);

  const values = formatValues(
    'values' in row
      ? (row.values ?? [])
      : columns.map((c) => ({
          id: c.id,
          title: c.title,
          total: sum(subRows.map((s) => s.values[c.id]?.total ?? 0)),
        }))
  );

  return {
    id: rowId,
    title: row.title,
    total: row.total,
    values,
    subRows,
    columns,
    type,
    accountId: type === 'account' ? row.id : undefined,
    categoryId: type === 'category' ? row.id : undefined,
  };
};

export const useBalanceSheetTableQuery = () => {
  const { filter } = useBalanceSheetTableFilter();

  const hideNonZeroRows = filter.view?.includes('non-zero-rows');

  const query = useApiQuery(
    async (api, signal, args): Promise<BalanceSheetTableData> => {
      const balanceSheet = await api
        .GET('/reports/balance-sheet', {
          params: {
            query: getBalanceSheetQueryFilterParams(args),
          },
          signal,
        })
        .then((res) => {
          if (res.error) throw res.error;

          return res.data.balanceSheet;
        });

      if (balanceSheet) {
        const rows = [balanceSheet].map((row) => formatAmbigiousRow(row));
        return {
          columns: rows?.[0]?.columns ?? [],
          rows: rows,
        };
      }

      return {
        columns: [],
        rows: [],
      };
    },
    {
      variables: {
        startDate: filter.startDate,
        endDate: filter.endDate,
        ledger: filter.ledger,
        type: filter.type,
      },
      storybookQueryKey: STORYBOOK_BALANCE_SHEET_TABLE_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'balance-sheet-report',
      queryKeys: ['journalEntries', 'reports'],
    }
  );

  const data = useMemo(() => {
    if (hideNonZeroRows && query.data) {
      return {
        ...query.data,
        rows: filterNonZeroRows(query.data.rows),
      };
    }

    return query.data;
  }, [query.data, hideNonZeroRows]);

  return {
    ...query,
    data,
  };
};
